import {
  useMutationMemberEditAsMemberNew,
  useQueryMemberGetCurrentMembershipSubscriptionAsMember,
} from "@gymflow/api";
import { getPatch, membershipHelper } from "@gymflow/helpers";
import { Gender, UserMemberBean } from "@gymflow/types";
import {
  Button,
  CloseIcon,
  FormInput,
  FormRadioButtonGroup,
  ImageInputWithModal,
  Trash2Icon,
} from "apps/portal/src/components/atoms";
import { FormSingleDatePicker } from "apps/portal/src/components/atoms/form/FormSingleDatePicker";
import { FormikProvider, useFormik } from "formik";
import { DateTime } from "luxon";
import { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { ModalContext, useClubSettings } from "../../../../../../providers";
import { AuthenticationContext } from "../../../../../../providers/authentication/AuthenticationContext";
import { ToastContext } from "../../../../../../providers/ToastProvider/context";
import useGymflowModels from "../../../../../../store";
import { FormPhoneNumberInput } from "../../../../../atoms/form/text/FormPhoneNumberInput";
import { DeleteMemberModal } from "../../../../../templates/DeleteMemberModal";
import { generateMemberSchema } from "./MemberSchema";

type MemberSchema = ReturnType<typeof generateMemberSchema>;
type MemberFormValues = z.infer<MemberSchema>;

export type EditMemberFormProps = {
  originalMember: UserMemberBean;
  onClose: () => void;
};

export const EditMemberForm = ({
  originalMember,
  onClose,
}: EditMemberFormProps) => {
  const { logout, userId } = useContext(AuthenticationContext);
  const { t } = useTranslation();
  const { properties } = useClubSettings();
  const { api } = useGymflowModels();

  const { mutateAsync: updateUser } = useMutationMemberEditAsMemberNew({ api });

  const { data: activeSubscription } =
    useQueryMemberGetCurrentMembershipSubscriptionAsMember({
      api,
    });

  const MemberSchema = useMemo(() => {
    return generateMemberSchema({
      postcodeCountry: properties.postalCodeCountry,
    });
  }, [properties.postalCodeCountry]);
  const initialValues = useMemo(() => {
    const initialValues = {
      email: undefined,
      firstName: undefined,
      lastName: undefined,
      mobileNumber: undefined,
      postCode: undefined,
      addressLine1: undefined,
      addressLine2: undefined,
      city: undefined,
      emailCommunication: undefined,
      smsCommunication: undefined,
      pushCommunication: undefined,
      emergencyContactName: undefined,
      emergencyContact: undefined,
      isClubWaiverAccepted: undefined,
      personalNumber: undefined,
    };
    return {
      ...initialValues,
      ...originalMember,
      dateBirth: originalMember.dateBirth
        ? DateTime.fromISO(originalMember.dateBirth).toJSDate()
        : undefined,
      gender: originalMember.gender ?? "PREFER_NOT_TO_SAY",
      picture: originalMember.picture
        ? {
            url: originalMember.picture,
          }
        : undefined,
    };
  }, [originalMember]);

  const getIsDisableEditField = useCallback(
    (fieldId: keyof MemberFormValues) => {
      return membershipHelper.getIsDisableEditFieldBecauseOfActiveMembershipNew(
        false,
        fieldId,
        initialValues,
        activeSubscription,
      );
    },
    [activeSubscription, initialValues],
  );

  const formik = useFormik<MemberFormValues>({
    initialValues,
    validationSchema: toFormikValidationSchema(MemberSchema),
    onSubmit: async (values: MemberFormValues) => {
      const toPost = {
        ...values,
        picture: undefined,
        dateBirth: values.dateBirth
          ? DateTime.fromJSDate(values.dateBirth).toISODate()!
          : undefined,
      };
      const toPatch = getPatch(originalMember, {
        ...toPost,
      });
      if (values.picture?.url !== originalMember.picture) {
        await updateUser({
          picture: values.picture?.blob,
        });
      }
      await updateUser({
        patchedFields: toPatch,
      });
      onClose();
    },
  });
  const { setModal, hide } = useContext(ModalContext);
  const { toast } = useContext(ToastContext);
  return (
    <FormikProvider value={formik}>
      <div className="flex h-full flex-col overflow-hidden">
        <div className="flex flex-col justify-between border-b border-gray-200 p-6 dark:border-gray-800">
          <div className="flex flex-row items-center justify-between">
            <div className="dark:text-gray-0 text-lg font-semibold text-gray-950">
              {t("pages.hostedPagesProfile.editMember.title")}
            </div>
            <Button
              intent="transparent"
              className="h-fit min-w-0 p-0"
              onClick={onClose}
            >
              <CloseIcon />
            </Button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex w-full items-center justify-center pb-4">
            <ImageInputWithModal
              value={formik.values["picture"]}
              onChange={(blob) => {
                const url = URL.createObjectURL(blob);
                if (blob) {
                  formik.setFieldValue("picture", { url, blob });
                } else {
                  formik.setFieldValue("picture", undefined);
                }
              }}
              modalTitle={t("pages.hostedPagesProfile.editMember.image")}
              disabled={getIsDisableEditField("picture")}
              placeholder={`${formik.values.firstName.charAt(0)}${formik.values.lastName.charAt(
                0,
              )}`}
            />
          </div>
          <div className="flex flex-col gap-4">
            <div className="flex flex-row gap-4">
              <FormInput<MemberFormValues>
                name="firstName"
                label={t("portal.common.userMemberForm.firstName")}
                placeholder={t("portal.common.userMemberForm.firstName")}
                isRequired
                disabled={getIsDisableEditField("firstName")}
              />
              <FormInput<MemberFormValues>
                name="lastName"
                label={t("portal.common.userMemberForm.lastName")}
                placeholder={t("portal.common.userMemberForm.lastName")}
                isRequired
                disabled={getIsDisableEditField("lastName")}
              />
            </div>
            <FormInput<MemberFormValues>
              name="email"
              label={t("portal.common.userMemberForm.email")}
              placeholder={t("portal.common.userMemberForm.email")}
              isRequired
              disabled={getIsDisableEditField("email")}
            />
            <FormPhoneNumberInput<MemberFormValues>
              name="mobileNumber"
              label={t("portal.common.userMemberForm.mobileNumber")}
              placeholder={t("portal.common.userMemberForm.mobileNumber")}
              isRequired
              disabled={getIsDisableEditField("mobileNumber")}
            />
            <FormSingleDatePicker<MemberFormValues>
              name="dateBirth"
              label={t("portal.common.userMemberForm.dateOfBirth")}
              isDisabled={getIsDisableEditField("dateBirth")}
              captionLayout="dropdown"
            />

            <FormRadioButtonGroup<MemberFormValues, Gender>
              name="gender"
              label="Gender"
              options={[
                {
                  label: t("portal.common.forms.genderOptions.male"),
                  value: "MALE",
                },
                {
                  label: t("portal.common.forms.genderOptions.female"),
                  value: "FEMALE",
                },
                {
                  label: t("portal.common.forms.genderOptions.other"),
                  value: "PREFER_NOT_TO_SAY",
                },
              ]}
              disabled={getIsDisableEditField("gender")}
            />
            <FormInput<MemberFormValues>
              name="addressLine1"
              label={t("portal.common.userMemberForm.addressLine1")}
              placeholder={t("portal.common.userMemberForm.addressLine1")}
              disabled={getIsDisableEditField("addressLine1")}
            />
            <FormInput<MemberFormValues>
              name="addressLine2"
              label={t("portal.common.userMemberForm.addressLine2")}
              placeholder={t("portal.common.userMemberForm.addressLine2")}
              disabled={getIsDisableEditField("addressLine2")}
            />

            <div className="flex flex-row gap-4">
              <FormInput<MemberFormValues>
                name="city"
                label={t("portal.common.userMemberForm.city")}
                placeholder={t("portal.common.userMemberForm.city")}
                disabled={getIsDisableEditField("city")}
              />
              <FormInput<MemberFormValues>
                name="postCode"
                label={t("portal.common.userMemberForm.postCode")}
                placeholder={t("portal.common.userMemberForm.postCode")}
                disabled={getIsDisableEditField("postCode")}
              />
            </div>
            <FormInput<MemberFormValues>
              name="personalNumber"
              label={t("portal.common.userMemberForm.personalNumber")}
              placeholder={t("portal.common.userMemberForm.personalNumber")}
              disabled={getIsDisableEditField("personalNumber")}
            />
            <div className="shadow-xs flex flex-col gap-4 rounded-2xl border border-gray-200 p-4 dark:border-gray-800">
              <div className="dark:text-gray-0 font-semibold text-gray-950">
                {t("portal.common.userMemberForm.emergencyContact")}
              </div>
              <FormInput<MemberFormValues>
                name="emergencyContactName"
                label={t("portal.common.userMemberForm.emergencyContactName")}
                placeholder={t(
                  "portal.common.userMemberForm.emergencyContactName",
                )}
                disabled={getIsDisableEditField("emergencyContactName")}
              />
              <FormPhoneNumberInput<MemberFormValues>
                name="emergencyContact"
                label={t("portal.common.userMemberForm.emergencyContact")}
                placeholder={t("portal.common.userMemberForm.emergencyContact")}
                disabled={getIsDisableEditField("emergencyContact")}
              />
            </div>
            {userId && (
              <Button
                intent="error-outline"
                size="small"
                className="w-fit gap-1.5 self-center"
                onClick={() => {
                  setModal(
                    <DeleteMemberModal
                      as="member"
                      memberId={userId}
                      onConfirm={() => {
                        hide();
                        toast({
                          message: t(
                            "page.hostedPagesProfile.editMember.deletedMember",
                          ),
                        });
                        logout();
                      }}
                      onClose={() => {
                        hide();
                      }}
                    />,
                  );
                }}
              >
                <Trash2Icon />
                {t("pages.hostedPagesProfile.editMember.delete")}
              </Button>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between gap-4 border-t border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-900">
          <Button intent="default" className="w-full" onClick={onClose}>
            Cancel
          </Button>
          <Button
            intent="secondary"
            className="w-full"
            onClick={async () => await formik.submitForm()}
          >
            {t("portal.common.save")}
          </Button>
        </div>
      </div>
    </FormikProvider>
  );
};
